import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';

const SIDEBAR_SECTIONS = [
  {
    id: 'command',
    title: 'Command Center',
    items: [
      { id: 'dashboard', icon: '📊', label: 'Dashboard' },
      { id: 'agents', icon: '⚔️', label: 'Living War Room', agentView: 'alliance' },
      { id: 'agents', icon: '🏛️', label: 'Hire an Agent', agentView: 'marketplace', tier: 'pro' },
    ],
  },
  {
    id: 'create',
    title: 'Create',
    items: [
      { id: 'generate', icon: '🚀', label: 'Content Forge' },
      { id: 'multiply', icon: '⚡', label: 'Content Multiplier', tier: 'pro' },
      { id: 'content-brief', icon: '💡', label: 'Content Brief', tier: 'pro' },
      { id: 'content-calendar', icon: '📅', label: 'Content Calendar', tier: 'pro' },
      { id: 'content-explosion', icon: '🎨', label: 'Multi-Format', tier: 'pro' },
      { id: 'bulk', icon: '⚡', label: 'Bulk Generator', tier: 'enterprise' },
    ],
  },
  {
    id: 'analyze',
    title: 'Analyze',
    items: [
      { id: 'competitor-analyzer', icon: '🔍', label: 'Beat Competition', tier: 'pro' },
      { id: 'website-analyzer', icon: '🔍', label: 'Website Analyzer', tier: 'pro' },
      { id: 'seo', icon: '🎯', label: 'SEO Optimizer', tier: 'pro' },
      { id: 'predictor', icon: '🔮', label: 'Performance Predictor', tier: 'pro' },
      { id: 'viral', icon: '🔥', label: 'Viral Analyzer', tier: 'pro' },
      { id: 'content-decay', icon: '⚠️', label: 'Content Health', tier: 'pro' },
      { id: 'sentiment-predictor', icon: '😊', label: 'Sentiment Predictor', tier: 'pro' },
    ],
  },
  {
    id: 'distribute',
    title: 'Distribute',
    items: [
      { id: 'ads-dashboard', icon: '📢', label: 'Ads Dashboard', tier: 'enterprise' },
      { id: 'autopilot', icon: '🤖', label: 'Auto-Pilot', tier: 'enterprise' },
      { id: 'zapier', icon: '🔗', label: 'Zapier Integration', tier: 'enterprise' },
    ],
  },
  {
    id: 'leads',
    title: 'Leads & Outreach',
    items: [
      { id: 'artemis', icon: '🏹', label: 'Artemis', tier: 'pro' },
      { id: 'quora', icon: '🏆', label: 'Quora Goldmine' },
    ],
  },
  {
    id: 'intelligence',
    title: 'Intelligence',
    items: [
      { id: 'trending', icon: '📈', label: 'Trending Topics', tier: 'pro' },
      { id: 'analytics', icon: '📊', label: 'Analytics', tier: 'pro' },
      { id: 'roi-calculator', icon: '💰', label: 'ROI Calculator', tier: 'pro' },
      { id: 'demo-analytics', icon: '📊', label: 'Demo Analytics', tier: 'enterprise' },
      { id: 'digest-settings', icon: '📧', label: 'Weekly Digest' },
    ],
  },
  {
    id: 'settings',
    title: 'Settings & Tools',
    items: [
      { id: 'api-keys', icon: '🔐', label: 'API Keys', tier: 'enterprise' },
      { id: 'referrals', icon: '🎁', label: 'Referrals' },
      { id: 'free-resources', icon: '📚', label: 'Free Resources', link: '/free-downloads' },
    ],
  },
];

const AppSidebar = ({ activeTab, setActiveTab, setAgentView, userTier, isMobileOpen, setMobileOpen }) => {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar_collapsed');
      if (saved !== null) return saved === 'true';
      return window.innerWidth < 1024;
    }
    return false;
  });
  const [expandedSections, setExpandedSections] = useState(() => {
    // Default: all expanded
    return SIDEBAR_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {});
  });

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(collapsed));
  }, [collapsed]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const canAccess = (tier) => {
    if (!tier) return true;
    if (tier === 'pro') return userTier === 'pro' || userTier === 'enterprise';
    if (tier === 'enterprise') return userTier === 'enterprise';
    return true;
  };

  const handleItemClick = (item) => {
    if (item.link) return; // Links handle themselves
    if (item.agentView) {
      setActiveTab('agents');
      setAgentView(item.agentView);
    } else {
      setActiveTab(item.id);
    }
    setMobileOpen(false);
  };

  const isActive = (item) => {
    if (item.agentView === 'alliance') return activeTab === 'agents'; // Simplify — War Room is the agents tab
    if (item.agentView === 'marketplace') return activeTab === 'agents';
    return activeTab === item.id;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Collapse Toggle */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-gray-800/50">
        {!collapsed && <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Navigation</span>}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-white transition-colors"
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
        {SIDEBAR_SECTIONS.map(section => {
          const accessibleItems = section.items.filter(item => canAccess(item.tier));
          if (accessibleItems.length === 0) return null;

          return (
            <div key={section.id} className="mb-1">
              {/* Section Header */}
              {!collapsed && (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors"
                >
                  <span>{section.title}</span>
                  <ChevronRight className={`w-3 h-3 transition-transform ${expandedSections[section.id] ? 'rotate-90' : ''}`} />
                </button>
              )}

              {/* Section Items */}
              {(collapsed || expandedSections[section.id]) && (
                <div className="space-y-0.5 px-2">
                  {accessibleItems.map((item, idx) => {
                    const active = isActive(item);

                    if (item.link) {
                      return (
                        <Link
                          key={`${section.id}-${idx}`}
                          to={item.link}
                          className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                            collapsed ? 'justify-center' : ''
                          } text-gray-400 hover:text-white hover:bg-gray-800/50`}
                          title={collapsed ? item.label : undefined}
                        >
                          <span className="text-base flex-shrink-0">{item.icon}</span>
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </Link>
                      );
                    }

                    return (
                      <button
                        key={`${section.id}-${idx}`}
                        onClick={() => handleItemClick(item)}
                        className={`group w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                          collapsed ? 'justify-center' : ''
                        } ${
                          active
                            ? 'bg-gradient-to-r from-purple-600/20 to-indigo-600/20 text-purple-300 border border-purple-500/30'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                        }`}
                        title={collapsed ? item.label : undefined}
                        data-testid={`sidebar-item-${item.id}`}
                      >
                        <span className="text-base flex-shrink-0">{item.icon}</span>
                        {!collapsed && (
                          <span className="truncate font-medium">{item.label}</span>
                        )}
                        {active && !collapsed && (
                          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile Drawer */}
      <aside
        className={`fixed top-0 left-0 h-full z-50 lg:hidden transform transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } w-64 bg-gray-950 border-r border-gray-800`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-sm font-bold text-white">Menu</span>
          <button onClick={() => setMobileOpen(false)} className="p-1 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {sidebarContent}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col h-screen sticky top-0 transition-all duration-300 bg-gray-950/95 backdrop-blur-md border-r border-gray-800/50 ${
          collapsed ? 'w-[52px]' : 'w-56'
        }`}
        data-testid="app-sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default AppSidebar;
